import { cn } from '../lib/cn';

/** Atlas workspace mark and wordmark. */

const sizeMap = {
  sm: { symbol: 16, text: 14, gap: 'gap-1.5' },
  md: { symbol: 20, text: 18, gap: 'gap-2' },
  lg: { symbol: 28, text: 22, gap: 'gap-2.5' },
  xl: { symbol: 40, text: 32, gap: 'gap-3' },
} as const;

export interface LogoProps {
  size?: keyof typeof sizeMap;
  showWordmark?: boolean;
  wordmark?: string;
  className?: string;
  /** Monochrome — uses currentColor. Useful on accent/dark backgrounds. */
  mono?: boolean;
}

export function Logo({
  size = 'md',
  showWordmark = true,
  wordmark = 'Atlas',
  className,
  mono = false,
}: LogoProps) {
  const s = sizeMap[size];
  return (
    <span className={cn('inline-flex items-center font-sans font-bold', s.gap, className)}>
      <LogoSymbol size={s.symbol} mono={mono} />
      {showWordmark ? (
        <span
          className="leading-none tracking-tight"
          style={{ fontSize: s.text, fontWeight: 700 }}
        >
          {wordmark}
        </span>
      ) : null}
    </span>
  );
}

interface LogoSymbolProps {
  size: number;
  mono?: boolean;
  className?: string;
}

/** Unboxed Atlas mark used by the collapsed rail and the full lockup. */
export function LogoSymbol({ size, mono, className }: LogoSymbolProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      role="img"
      aria-label="Atlas logo"
      className={cn('shrink-0', className)}
    >
      <path
        d="M3.25 18.5 10.4 5h3.2l7.15 13.5h-3.65L12 8.85 6.9 18.5z"
        fill={mono ? 'currentColor' : 'var(--text-primary)'}
      />
    </svg>
  );
}
